import type { Character, ToggleState } from "../character";
import type { DerivedSheet, SpeedView } from "../engine";
import { overrideTargets, signed } from "../engine";
import type { SessionMaxima } from "../session";
import { RollButton } from "./roll-button";
import { useSheetActions } from "./sheet-context";
import type { StatDetail } from "./stat-detail";
import { abilityDetail, hitPointsDetail, speedDetail, statisticDetail } from "./stat-detail";
import { iwrSourceLabel } from "./iwr-editor";
import { StatTile, TileRow } from "./tiles";

interface Props {
  character: Character;
  sheet: DerivedSheet;
  maxima: SessionMaxima | null;
  busy: boolean;
  onStat: (detail: StatDetail) => void;
  /** Toggle a RollOption: the Session's while one is open, else the Character's. */
  onToggle: (key: string, change: ToggleState) => Promise<void>;
  /** Open the editor for immunities, weaknesses and resistances entered by hand (#22). */
  onIWR: () => void;
}

/**
 * The Overview tab (issue #14): attributes, HP, AC, saves, Perception, class and spell DCs,
 * speeds, senses, traits, IWR, resources and the RollOption toggles, every number tappable for
 * its breakdown and Override; the notices the derived sheet raises (missing Entries, open Choices,
 * a battle form, Unsupported and failed Rule Elements).
 */
export function Overview({ character, sheet, maxima, busy, onStat, onToggle, onIWR }: Props) {
  const { openEntry } = useSheetActions();
  const ctx = { sheet };
  const session = character.session;
  const speeds = Object.values(sheet.speeds).filter((sp): sp is SpeedView => sp !== null);
  const openPrompts = sheet.prompts.filter((p) => p.open);
  const iwr = sheet.iwr;
  const resources = sheet.resources;
  const hasResources = resources.tempHP.length > 0 || resources.fastHealing.length > 0 || resources.loseHitPoints.length > 0 || Object.keys(resources.special).length > 0 || Object.keys(resources.crafting).length > 0;
  const hpValue = session && maxima ? `${session.hp.value} / ${maxima.hp.max}` : String(sheet.hp.value);
  const hpSub = session && session.hp.temp > 0 ? `+${session.hp.temp} temp` : sheet.hp.override ? `engine ${sheet.hp.derived}` : null;
  const sub = (view: { override: { value: number } | null; derived: number }, dc = false) => (view.override ? `engine ${dc ? view.derived : signed(view.derived)}` : null);

  return (
    <div class="overview">
      {sheet.missing.length > 0 && <p class="notice">Entries not found in this Snapshot: {sheet.missing.map((m) => m.uuid).join(", ")}</p>}
      {openPrompts.length > 0 && (
        <p class="notice">
          {openPrompts.length === 1 ? "One Choice is open" : `${openPrompts.length} Choices are open`}: {openPrompts.map((p) => `${p.name} (${p.prompt})`).join("; ")}. Answer them in the Build tab.
        </p>
      )}
      {sheet.battleForm && <p class="notice">Battle form in force: <button type="button" class="linkish" onClick={() => openEntry(sheet.battleForm!.uuid)}>{sheet.battleForm.name}</button>{sheet.battleForm.ownUnarmed ? " (own unarmed attacks)" : ""}. AC, skills, speeds and strikes are the form's.</p>}
      {sheet.danglingOverrides.length > 0 && (
        <p class="notice">Overrides naming no statistic: {sheet.danglingOverrides.map((o) => o.statistic).join(", ")}. Known keys: {overrideTargets(sheet).map((t) => t.key).join(", ")}.</p>
      )}

      <div class="overview-grid">
        <div class="overview-main">
          <TileRow>
            <StatTile label="HP" value={hpValue} sub={hpSub} overridden={!!sheet.hp.override} onTap={() => onStat(hitPointsDetail(sheet.hp, ctx))} />
            <StatTile label="AC" value={String(sheet.ac.value)} sub={sub(sheet.ac, true)} overridden={!!sheet.ac.override} onTap={() => onStat(statisticDetail(sheet.ac, ctx, { dc: true, selectors: ["ac", "dex-based"] }))} />
            {sheet.classDC && <StatTile label="Class DC" value={String(sheet.classDC.value)} sub={sub(sheet.classDC, true)} overridden={!!sheet.classDC.override} onTap={() => onStat(statisticDetail(sheet.classDC!, ctx, { dc: true, selectors: ["class-dc", "class"] }))} />}
            <StatTile label="Perception" value={signed(sheet.perception.value)} sub={sub(sheet.perception)} overridden={!!sheet.perception.override} onTap={() => onStat(statisticDetail(sheet.perception, ctx, { selectors: ["perception", "wis-based"] }))} />
            {speeds.map((sp) => <StatTile key={sp.type} label={sp.label.replace(" Speed", "")} value={`${sp.value} ft`} sub={sp.override ? `engine ${sp.derived}` : null} overridden={!!sp.override} onTap={() => onStat(speedDetail(sp, ctx))} />)}
          </TileRow>
          <TileRow title="Saving throws">
            {Object.values(sheet.saves).map((s) => <StatTile key={s.slug} label={s.label} value={signed(s.value)} sub={sub(s)} overridden={!!s.override} onTap={() => onStat(statisticDetail(s, ctx, { selectors: [s.slug, "saving-throw", `${s.attribute ?? ""}-based`] }))} />)}
          </TileRow>
          <div class="tile-row">
            <h3 class="row-title">Roll</h3>
            <div class="roll-strip">
              {[sheet.perception, ...Object.values(sheet.saves), sheet.initiative].map((s) => (
                <RollButton key={s.slug} class="roll-check" request={{ kind: "check", type: s.slug, statistic: s.slug, label: s.label, modifier: s.value, dc: null, against: null, basic: false, traits: [], options: [], name: null }}>
                  <span class="roll-label">{s.label}</span>
                  <span class="roll-mod">{signed(s.value)}</span>
                </RollButton>
              ))}
              <RollButton class="roll-check" request={{ kind: "check", type: "flat", statistic: null, label: "Flat check", modifier: 0, dc: null, against: null, basic: false, traits: [], options: [], name: null }}>
                <span class="roll-label">Flat check</span>
              </RollButton>
            </div>
            <p class="muted">Tap to roll, hold to enter a physical die. Every other check rolls from the Skills, Strikes and Spells tabs and from the Roll buttons in an Entry's text.</p>
          </div>
          <TileRow title="Attributes">
            {Object.values(sheet.abilities).map((a) => <StatTile key={a.attribute} label={a.label.slice(0, 3)} value={signed(a.value)} sub={sub(a)} overridden={!!a.override} onTap={() => onStat(abilityDetail(a, ctx))} title={a.label} />)}
          </TileRow>
          {sheet.spellcasting.some((e) => e.statistic && !e.statisticOf) && (
            <TileRow title="Spellcasting">
              {sheet.spellcasting.filter((e) => e.statistic && !e.statisticOf).map((e) => (
                <span key={e.id} class="stat-pair">
                  <StatTile label={`${e.traditionLabel} attack`} value={signed(e.statistic!.value)} sub={sub(e.statistic!)} overridden={!!e.statistic!.override} onTap={() => onStat(statisticDetail(e.statistic!, ctx, { selectors: ["spell-attack", "spell-attack-roll", "attack-roll", e.tradition ? `${e.tradition}-spell-attack` : "spell-attack"], label: `${e.name} attack` }))} />
                  <StatTile label={`${e.traditionLabel} DC`} value={String(e.statistic!.dc)} onTap={() => onStat(statisticDetail(e.statistic!, ctx, { dc: true, selectors: ["spell-dc", e.tradition ? `${e.tradition}-spell-dc` : "spell-dc"], label: `${e.name} DC` }))} />
                </span>
              ))}
            </TileRow>
          )}
          {Object.keys(sheet.specialStatistics).length > 0 && (
            <TileRow title="Other statistics">
              {Object.values(sheet.specialStatistics).map((s) => <StatTile key={s.slug} label={s.label} value={signed(s.value)} sub={sub(s)} overridden={!!s.override} onTap={() => onStat(statisticDetail(s, ctx))} />)}
            </TileRow>
          )}
        </div>
        <div class="overview-side">
          <section class="card facts">
            <h3>About</h3>
            <dl>
              <dt>Level</dt><dd>{sheet.level}</dd>
              <dt>Size</dt><dd>{sheet.size}</dd>
              <dt>Reach</dt><dd>{sheet.reach.base} ft{sheet.reach.manipulate !== sheet.reach.base ? ` (${sheet.reach.manipulate} ft to manipulate)` : ""}</dd>
              <dt>Traits</dt><dd>{sheet.traits.join(", ") || "none"}</dd>
              <dt>Senses</dt><dd>{sheet.senses.length > 0 ? sheet.senses.map((s) => s.display ?? s.label).join(", ") : "none beyond sight"}</dd>
              <dt>Languages</dt><dd>{sheet.languages.join(", ") || "none recorded"}</dd>
              {sheet.dexterityCap && <><dt>Dex cap</dt><dd>+{sheet.dexterityCap.value} ({sheet.dexterityCap.source})</dd></>}
              {sheet.hp.negativeHealing && <><dt>Healing</dt><dd>Void healing (negative healing)</dd></>}
            </dl>
          </section>
          <section class="card facts iwr-card">
            <div class="row">
              <h3>Immunities, weaknesses, resistances</h3>
              <button type="button" disabled={busy} onClick={onIWR}>Edit</button>
            </div>
            {iwr.immunities.length === 0 && iwr.weaknesses.length === 0 && iwr.resistances.length === 0 ? <p class="muted">None. Add one by hand for what the rules do not derive.</p> : (
              <dl>
                {([["Immune", iwr.immunities], ["Weak", iwr.weaknesses], ["Resist", iwr.resistances]] as const).map(([title, list]) => list.length > 0 && (
                  <>
                    <dt key={`${title}-t`}>{title}</dt>
                    <dd key={`${title}-d`}>{list.map((i, n) => <span key={n} class="iwr-entry">{n > 0 ? ", " : ""}{i.label}{iwrSourceLabel(i) && <span class={`tag${i.manual ? " open" : ""}`} title={iwrSourceLabel(i)}>{i.manual ? "by hand" : i.sourceName}</span>}</span>)}</dd>
                  </>
                ))}
              </dl>
            )}
          </section>
          {hasResources && (
            <section class="card facts">
              <h3>Resources</h3>
              <ul class="plain">
                {resources.tempHP.map((t, i) => <li key={`thp-${i}`} class={t.enabled ? "" : "muted"}>Temporary HP {t.value} · {t.sourceName}{t.events.onTurnStart ? " (renews each turn)" : ""}{t.enabled ? "" : " · off"}</li>)}
                {resources.fastHealing.map((f, i) => <li key={`fh-${i}`} class={f.enabled ? "" : "muted"}>{f.type === "regeneration" ? "Regeneration" : "Fast healing"} {f.value} · {f.sourceName}{f.details ? ` · ${f.details}` : ""}{f.enabled ? "" : " · off"}</li>)}
                {resources.loseHitPoints.map((l, i) => <li key={`lhp-${i}`}>Lose {l.value} HP{l.recoverable ? "" : " (unrecoverable)"} · {l.sourceName}</li>)}
                {Object.values(resources.special).map((r) => <li key={r.slug}>{r.label}: {r.value} / {r.max}{r.level !== null ? ` (level ${r.level})` : ""} · {r.sourceName}</li>)}
                {Object.values(resources.crafting).map((c) => <li key={c.slug}>{c.label}: {c.maxSlots !== null ? `${c.maxSlots} formula slots` : "crafting"}, up to level {c.maxItemLevel}{c.isDailyPrep ? ", daily preparations" : ""} · {c.sourceName}</li>)}
              </ul>
            </section>
          )}
          {sheet.auras.length > 0 && (
            <section class="card facts">
              <h3>Auras</h3>
              <ul class="plain">
                {sheet.auras.map((a) => <li key={a.slug}>{a.sourceName}: {a.radius} ft{a.traits.length > 0 ? ` (${a.traits.join(", ")})` : ""} · {a.effects.map((e) => `${e.name ?? e.uuid} for ${e.affects}${e.appliedToSelf ? ", applied to you" : ""}`).join("; ")}</li>)}
              </ul>
            </section>
          )}
        </div>
      </div>

      {sheet.toggles.length > 0 && (
        <section class="card toggles">
          <h3>Options</h3>
          <ul class="plain">
            {sheet.toggles.map((t) => {
              const key = `${t.domain}:${t.option}`;
              return (
                <li key={`${t.itemUuid}:${t.option}`} class="toggle-row">
                  <label class="toggle">
                    <input type="checkbox" checked={t.checked} disabled={busy || !t.enabled || t.alwaysActive} onChange={(e) => void onToggle(key, { checked: (e.currentTarget as HTMLInputElement).checked })} />
                    <span><button type="button" class="linkish" onClick={() => openEntry(t.itemUuid)}>{t.itemName}</button>: {t.label}{t.enabled ? "" : <span class="muted"> (disabled)</span>}</span>
                  </label>
                  {t.suboptions.length > 0 && (
                    <select aria-label={`${t.label} option`} value={t.selection ?? ""} disabled={busy} onChange={(e) => void onToggle(key, { selection: (e.currentTarget as HTMLSelectElement).value })}>
                      {t.suboptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <details class="card" open={sheet.unsupported.length > 0 || sheet.problems.length > 0}>
        <summary>Unsupported Rule Elements ({sheet.unsupported.length}){sheet.problems.length > 0 ? ` · could not be applied (${sheet.problems.length})` : ""}</summary>
        {sheet.unsupported.length === 0 ? <p class="muted">Every Rule Element on this Character is implemented.</p> : (
          <ul class="plain">
            {sheet.unsupported.map((u) => (
              <li key={`${u.uuid}:${u.index}`}>
                <span class="tag warn">{u.key}</span> <button type="button" class="linkish" onClick={() => openEntry(u.uuid)}>{u.name}</button>{u.selector ? <span class="muted"> · {u.selector}</span> : null}{u.support === "unknown" ? <span class="muted"> · unknown to Foundry</span> : u.support === "stopped" ? <span class="muted"> · stopped by the engine</span> : null}
              </li>
            ))}
          </ul>
        )}
        {sheet.problems.length > 0 && (
          <ul class="plain">
            {sheet.problems.map((p) => <li key={`${p.uuid}:${p.index}`}><span class="tag warn">{p.key}</span> <button type="button" class="linkish" onClick={() => openEntry(p.uuid)}>{p.name}</button>: {p.messages.join("; ")}</li>)}
          </ul>
        )}
      </details>
    </div>
  );
}
